import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const AppServerStatus = createContext();

let URL =
  process.env.REACT_APP_NODE_ENV === "PRODUCTION"
    ? process.env.REACT_APP_BACKEND_LIVE_URL
    : process.env.REACT_APP_BACKEND_LOCAL_URL;

const TIMEOUT_MS = 65000;
const RETRY_DELAY = 3000;
const MAX_RETRIES = 5;

export function AppServerStatusProvider({ children }) {
  const [status, setStatus] = useState("checking");
  const [attempt, setAttempt] = useState(0);

  const checkServer = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, TIMEOUT_MS);

    try {
      const res = await axios.get(`${URL}/api/server-status`, {
        signal: controller.signal,
        withCredentials: true,
        headers: {
          "x-crypt-value": process.env.REACT_APP_BACKEND_CRYPT_SECRET,
          "Content-Type": "application/json",
        },
      });

      console.log(res);

      clearTimeout(timeout);

      if (res.status !== 200) throw new Error("Not ready");

      setStatus("ready");
      return true;
    } catch {
      clearTimeout(timeout);
      return false;
    }
  };

  const wakeServer = async () => {
    setStatus("checking");

    for (let i = 0; i < MAX_RETRIES; i++) {
      setAttempt(i + 1);
      const ready = await checkServer();
      if (ready) return;

      await new Promise((res) => setTimeout(res, RETRY_DELAY));
    }

    setStatus("failed");
  };

  useEffect(() => {
    wakeServer();
  }, []);

  if (status === "checking") {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-11 col-sm-8 col-md-6 col-lg-5">
              <div className="card shadow-sm border-0 text-center p-4">
                <div
                  className="spinner-border text-primary mb-4 mx-auto"
                  role="status"
                />

                <h5 className="fw-semibold mb-2">
                  Preparing your experience...
                </h5>

                <p className="text-muted small mb-3">
                  We're getting everything ready for you. This may take a few
                  seconds.
                </p>

                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{
                      width: `${(attempt / MAX_RETRIES) * 100}%`,
                    }}
                  />
                </div>

                <p className="text-muted small mt-2 mb-0">
                  Please don’t refresh the page.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-11 col-sm-8 col-md-6 col-lg-5">
              <div className="card shadow-sm border-0 text-center p-4">
                <h5 className="fw-semibold mb-3">
                  We’re having trouble connecting
                </h5>

                <p className="text-muted small mb-4">
                  It’s taking longer than expected to connect. Please try again.
                </p>

                <button className="btn btn-primary w-100" onClick={wakeServer}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <AppServerStatus.Provider value={{ status }}>
      {children}
    </AppServerStatus.Provider>
  );
}

export function useAppServerStatus() {
  return useContext(AppServerStatus);
}
